import { PERMISSIONS } from '@/contexts/AuthContext';
import {
  LayoutDashboard,
  MessageSquare,
  TrendingUp,
  ThumbsUp,
  Users,
  BarChart3,
  BookOpen,
  FileText,
  Download,
  Link2,
  UserCog,
  Building2,
  Tags,
  FileQuestion,
  Trophy,
  Bell,
  Gift,
} from 'lucide-react';

const KB_MANAGE_PERMISSION = PERMISSIONS?.KB_MANAGE ?? 'kb_manage';

export const headerNavigationItems = [
  { path: '/overview', label: 'Overview', icon: LayoutDashboard },
  { path: '/kb/content', label: 'Content', icon: FileText },
  { path: '/kb/coverage', label: 'Coverage', icon: BarChart3 },
  { path: '/kb/topics', label: 'Topics', icon: Tags },
  { path: '/kb/gaps', label: 'Content Gaps', icon: FileQuestion },
  { path: '/people/employees', label: 'Employees', icon: Users },
  { path: '/people/groups', label: 'Groups', icon: Building2 },
  { path: '/people/content-team', label: 'Content Team', icon: UserCog },
  { path: '/people/adoption', label: 'Adoption', icon: BarChart3 },
  { path: '/conversations/activity', label: 'Activity', icon: MessageSquare },
  { path: '/conversations/trending', label: 'Topic Intelligence', icon: TrendingUp },
  { path: '/conversations/feedback', label: 'Feedback', icon: ThumbsUp },
  { path: '/engagement/leaderboard', label: 'Leaderboard', icon: Trophy },
  { path: '/engagement/notifications', label: 'Notifications', icon: Bell },
  { path: '/referrals', label: 'Refer & Earn', icon: Gift },
  { path: '/reports', label: 'Reports', icon: Download },
  { path: '/connectors', label: 'Connectors', icon: Link2 },
];

export const getNavigationConfig = (hasPermission, hasRole) => {
  const conversationChildren = [
    { path: '/conversations/activity', label: 'Activity', icon: MessageSquare },
    { path: '/conversations/trending', label: 'Topic Intelligence', icon: TrendingUp },
    { path: '/conversations/feedback', label: 'Feedback', icon: ThumbsUp },
  ];

  const baseNav = [
    {
      label: 'Overview',
      icon: LayoutDashboard,
      path: '/overview',
    },
    {
      label: 'Knowledge Base',
      icon: BookOpen,
      children: [
        ...(hasPermission(KB_MANAGE_PERMISSION)
          ? [{ path: '/kb/content', label: 'Content', icon: FileText }]
          : []),
        { path: '/kb/coverage', label: 'Coverage', icon: BarChart3 },
        ...(hasRole(['admin', 'approver'])
          ? [{ path: '/kb/topics', label: 'Topics', icon: Tags }]
          : []),
        ...(hasRole(['admin', 'approver', 'analyst'])
          ? [{ path: '/kb/gaps', label: 'Content Gaps', icon: FileQuestion }]
          : []),
      ],
    },
    {
      label: 'People',
      icon: Users,
      children: [
        ...(hasRole(['admin', 'approver', 'analyst', 'superadmin'])
          ? [{ path: '/people/employees', label: 'Employees', icon: Users }]
          : []),
        ...(hasRole(['admin', 'approver'])
          ? [{ path: '/people/groups', label: 'Groups', icon: Building2 }]
          : []),
        ...(hasRole(['admin', 'approver', 'analyst'])
          ? [{ path: '/people/content-team', label: 'Content Team', icon: UserCog }]
          : []),
        { path: '/people/adoption', label: 'Adoption', icon: BarChart3 },
      ],
    },
    {
      label: 'Conversations',
      icon: MessageSquare,
      children: conversationChildren,
    },
    {
      label: 'Engagement',
      icon: Trophy,
      children: [
        { path: '/engagement/leaderboard', label: 'Leaderboard', icon: Trophy },
        ...(hasRole(['admin', 'approver'])
          ? [{ path: '/engagement/notifications', label: 'Notifications', icon: Bell }]
          : []),
        { path: '/referrals', label: 'Refer & Earn', icon: Gift },
        ...(hasRole(['admin', 'approver', 'analyst'])
          ? [{ path: '/reports', label: 'Reports', icon: Download }]
          : []),
      ],
    },
    ...(hasRole(['admin'])
      ? [{
          label: 'Connectors',
          icon: Link2,
          path: '/connectors',
        }]
      : []),
  ];

  return baseNav.filter(item => !item.children || item.children.length > 0);
};

export function findNavigationItem(pathname, navigationConfig) {
  for (const item of navigationConfig) {
    if (item.path === pathname) return item;
    const child = item.children?.find((entry) => pathname === entry.path || pathname.startsWith(`${entry.path}/`));
    if (child) return child;
  }
  return null;
}
